import { FC, useEffect } from 'react';
import { gql, useApolloClient } from '@apollo/client';

import { Cart, emptyCart, useAppState } from 'src/components/context';
import { CART_FRAGMENT, PRODUCT_FRAGMENT } from 'src/fragments/fragments';
import { onSignedIn, onSignedOut } from 'src/utils/auth/authEvents';
import { cartToItems } from 'src/utils/cart/cartItems';
import { CART } from 'src/utils/cart/operations';

import { useCart } from './CartContext';

export const FILL_CART = gql`
  ${CART_FRAGMENT}
  ${PRODUCT_FRAGMENT}
  mutation FillCart($items: [CartItemInput!]!) {
    fillCart(input: { items: $items }) {
      cart {
        ...CartFragment
      }
    }
  }
`;

/**
 * Headless bridge between auth and cart state.
 *
 * AuthProvider sits above the cart providers — it needs Apollo, and the cart needs to
 * react to it — so it emits events rather than calling setCart directly.
 */
const CartAuthSync: FC = () => {
  const client = useApolloClient();
  const { setCart } = useAppState();
  const { flushCart } = useCart();

  useEffect(() => {
    const unsubscribeSignedIn = onSignedIn(async ({ guestCart }) => {
      try {
        /**
         * Barrier, not a write: the drawer flushes on close, so there is normally
         * nothing pending by the time anyone signs in. What this waits for is a flush
         * still in flight — its response carries the *guest* session's cart, and
         * landing after the merge below it would overwrite the customer's basket.
         */
        await flushCart();

        const guestItems = cartToItems(guestCart);

        // AuthProvider has already swapped in the customer's wooSessionToken, so the
        // server-side cart is now theirs. Nothing to merge if the guest had no items.
        if (guestItems.length === 0) {
          const { data } = await client.query({
            query: CART,
            fetchPolicy: 'network-only',
          });

          setCart(data?.cart ?? emptyCart);

          return;
        }

        /**
         * Only the guest's items are sent. fillCart ADDS to whatever the cart already
         * holds — verified against the live backend: a product at quantity 2, filled
         * with quantity 3, ends up at 5. So the customer's saved items are already
         * there, and passing a pre-merged list would count them twice.
         *
         * The server's additive behaviour is exactly the agreed rule: union the two
         * carts, summing quantities for a product present in both.
         */
        const { data } = await client.mutate({
          mutation: FILL_CART,
          variables: { items: guestItems },
        });

        setCart(data?.fillCart?.cart ?? emptyCart);
      } catch (error) {
        // Never drop the guest cart on a failed merge — leaving it in place is the
        // recoverable outcome.
        console.error('Could not merge carts after sign-in:', error);
      }
    });

    const unsubscribeSignedOut = onSignedOut(() => {
      // The Woo session token is already gone; clearing local cart state stops the
      // previous customer's basket showing up for whoever uses the device next.
      setCart(emptyCart);
    });

    return () => {
      unsubscribeSignedIn();
      unsubscribeSignedOut();
    };
  }, [client, flushCart, setCart]);

  return null;
};

export type { Cart };

export default CartAuthSync;
